import { AlertTriangle, LoaderCircle } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef } from "react";

interface ConfirmDeleteModalProps {
  title: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDeleteModal({ title, busy, error, onCancel, onConfirm }: ConfirmDeleteModalProps) {
  const confirmButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    confirmButton.current?.focus();
    return () => previouslyFocused?.focus();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && !busy) {
      event.preventDefault();
      onCancel();
    }
    if (event.key === "Enter" && !event.repeat && !busy) {
      event.preventDefault();
      onConfirm();
    }
  };

  return (
    <div
      className="confirm-modal-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && !busy && onCancel()}
      onKeyDown={handleKeyDown}
    >
      <section
        className="confirm-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-confirm-title"
        aria-describedby="delete-confirm-description"
      >
        <div className="confirm-modal-mark" aria-hidden="true"><AlertTriangle size={20} /></div>
        <p className="eyebrow">Permanent action</p>
        <h2 id="delete-confirm-title">Delete this work?</h2>
        <p id="delete-confirm-description">
          “{title}” will be removed from the archive together with its image files. This cannot be undone.
        </p>
        {error && <p className="confirm-modal-error" role="alert">{error}</p>}
        <div className="confirm-modal-actions">
          <button className="button secondary" type="button" disabled={busy} onClick={onCancel}>Cancel</button>
          <button ref={confirmButton} className="button danger-solid" type="button" disabled={busy} onClick={onConfirm}>
            {busy && <LoaderCircle size={17} className="spin" />}
            {busy ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </section>
    </div>
  );
}
